import { AuthRoleEnum } from '@codezo/graphql';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

interface PrivateRouteProps<TUser extends { role: AuthRoleEnum }> {
	children: React.ReactNode;
	requiredRole?: AuthRoleEnum;
	isAuthenticated: boolean;
	user: TUser | null;
}

export function PrivateRoute<TUser extends { role: AuthRoleEnum }>({
	children,
	requiredRole,
	isAuthenticated,
	user,
}: PrivateRouteProps<TUser>) {
	const location = useLocation();

	// Authenticated, but the session belongs to a different app's role
	// (e.g. an ADMIN refresh-token cookie hitting the Vetter app on a shared
	// localhost cookie jar). Reauth can't fix this — it would just re-issue
	// the same wrong-role session and we'd loop forever. Force a full logout
	// to the public auth app so the user can sign in fresh for THIS app.
	const wrongRole = isAuthenticated && !!requiredRole && user?.role !== requiredRole;

	useEffect(() => {
		if (wrongRole) {
			// Send the user straight to the public app's login page, not its
			// marketing landing page. `VITE_PUBLIC_APP_URL` is the public app's
			// root (e.g. http://localhost:3000), and the login page lives at
			// /auth/login within it (see apps/public/src/App.tsx).
			const publicAppUrl = import.meta.env.VITE_PUBLIC_APP_URL || '';
			window.location.replace(`${publicAppUrl}/auth/login?logout=true`);
		}
	}, [wrongRole]);

	if (!isAuthenticated) {
		return (
			<Navigate to={`/auth/reauth#prevPath=${location.pathname}${location.search}`} replace />
		);
	}

	if (wrongRole) {
		// Render nothing while the full-page redirect to the public app is in flight.
		return null;
	}

	return <>{children}</>;
}
